import React from "react";
import { IconCube, IconStack2 } from "@tabler/icons-react";
import { InternalProductSearchOutput } from "api-client";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import ProductChips from "features/BuildingProductEditor/components/ProductChips";
import { getCurrentReferenceUnitValue } from "features/MappingEditor/utils";
import { sidebarMenuStore } from "features/SidebarMenu/SidebarMenuStore";
import { getProductOrganisationName } from "utils/productDisplay";

function ProductInfo({
  item,
  detailsIcon,
  iconClassName,
  orderClassName,
  order,
  showOrder,
  ...props
}: {
  item: InternalProductSearchOutput;
  detailsIcon?: React.ReactElement | null;
  iconClassName?: string;
  orderClassName?: string;
  order?: number;
  showOrder?: boolean;
  containerClassName?: string;
}) {
  const { i18n, t } = useTranslation();
  const isEng = i18n.language === "en";
  const itemReferenceUnit = getCurrentReferenceUnitValue(
    item.reference_unit.toUpperCase()
  );

  if (!item) return <></>;

  function getIcon() {
    return (
      <div
        className={clsx(
          "rounded-full flex items-center justify-center min-w-[32px] min-h-[32px] text-teal-900",
          {
            "bg-teal-300 text-teal-900": item.type === "PRODUCT",
            "bg-cyan-300 text-cyan-900": item.type === "COMPONENT",
          }
        )}
      >
        {!showOrder && (
          <>
            {item.type === "PRODUCT" ? (
              <IconCube
                stroke={2.5}
                size={20}
                className={order ? iconClassName : ""}
              />
            ) : (
              <IconStack2
                stroke={2.5}
                size={20}
                className={order ? iconClassName : ""}
              />
            )}
          </>
        )}
        {(order || showOrder) && (
          <div
            className={clsx(
              " font-semibold text-lg",
              orderClassName,
              showOrder ? "block" : "hidden"
            )}
          >
            {order}
          </div>
        )}
      </div>
    );
  }

  function details() {
    const currentOrgId = sidebarMenuStore.currentOrganisation?.id;
    const belongsToCurrentOrg =
      !!item?.organisation_id && item.organisation_id === currentOrgId;

    return (
      <div className="flex">
        <div className="flex items-center px-4">{getIcon()}</div>
        <div className="w-full flex flex-col justify-between py-3">
          <div className="flex items-center">
            <ProductChips
              refUnit={
                isEng ? itemReferenceUnit?.name : itemReferenceUnit?.name_de
              }
              certifications={item?.compliant_certifications_names}
              conformity={item?.compliant_conformities_names}
              healthChecked={item?.health_checked}
              containerClassName="!mt-0"
              organisationName={getProductOrganisationName(item, currentOrgId)}
              createdByEmail={
                belongsToCurrentOrg ? item?.created_by_email : undefined
              }
            />
          </div>
          <div className="mt-2">
            <div className="flex pb-1">
              {item.din_category_number && (
                <span className="text-gray-500 text-sm">
                  {item.din_category_number}
                </span>
              )}
              {item.din_category && (
                <span className="text-gray-500 text-sm pl-1 overflow-hidden">
                  {item.din_category}
                </span>
              )}
            </div>
            <div className="text-base font-semibold text-gray-800 !cursor-auto pr-3 leading-[19px]">
              {item.name}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function productThumbnail() {
    const { thumbnail_url, diagram_url } = item;
    if (!item.thumbnail_url && !item.diagram_url) return null;

    const mainImage = thumbnail_url || diagram_url;
    return (
      <div className="relative min-w-[91px] max-w-[91px] overflow-hidden flex items-center">
        {/* Blurred background */}
        <img
          src={String(mainImage)}
          alt="cover"
          className="absolute inset-0 z-0 w-full h-full object-cover blur-md"
        />

        {/* Foreground */}
        <div className="relative z-1 min-w-[91px] max-h-[116px]">
          <img
            src={String(mainImage)}
            alt="thumbnail"
            className="h-full w-auto max-h-[91px] aspect-1 object-cover bg-gray-100"
          />

          {/* Diagram overlay */}
          {thumbnail_url && diagram_url && (
            <img
              src={String(diagram_url)}
              alt="diagram"
              className="absolute right-0 bottom-0 w-auto max-h-[45px] aspect-1 object-cover z-2"
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "flex overflow-hidden relative justify-between items-center gap-6 w-full transition group/infoRow",
        {
          "bg-gradient-to-r from-teal-50 hover:to-indigo-50":
            item.type === "PRODUCT",
          "bg-gradient-to-r from-cyan-50 hover:to-indigo-50":
            item.type === "COMPONENT",
        },
        props.containerClassName
      )}
    >
      <div className="flex">
        {productThumbnail()}
        {details()}
      </div>
      {detailsIcon && (
        <div
          className={clsx(
            "min-h-full hidden items-center pr-4 group-hover/infoRow:flex",
            "absolute right-0 top-0 transition bg-indigo-50"
          )}
        >
          <div className="w-10 bg-gradient-to-r from-transparent to-indigo-50"></div>
          {detailsIcon}
        </div>
      )}
    </div>
  );
}

export default observer(ProductInfo);
